import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useResolvedTheme } from '../ThemeProvider';

/**
 * Portal host for every overlay in the design system.
 *
 * ## Why this exists
 *
 * Overlays must portal into `#root`, not `document.body` — `tailwind.config.js`
 * sets `important: '#root'`, so a body-level portal escapes the scope of every
 * Tailwind utility (see the gotcha in MIGRATION.md).
 *
 * But `#root` is the *parent* of `.ds-root`, and every `--ds-*` token is
 * declared **on** `.ds-root`. So a plain `createPortal(…, #root)` lands as a
 * sibling of the scope it needs: every `var(--ds-…)` becomes invalid at
 * computed-value time and falls back to the property's initial value. In
 * practice that meant transparent dialogs, undimmed backdrops, no shadows, no
 * radius, no padding — and, because `preflight: false` leaves UA styles in
 * place while the resets are written as `.ds-root button` etc., native grey
 * buttons inside the dialog footer.
 *
 * So the portal keeps its `#root` host and re-creates the `.ds-root` scope
 * around its children. `display: contents` (see `.ds-portal-layer`) means the
 * wrapper generates no box at all — it exists purely to carry the `data-ds-*`
 * attributes the token declarations key off. Custom properties and inherited
 * properties still pass through it, and the fixed-position overlays inside are
 * unaffected, so stacking and z-index behaviour are unchanged.
 *
 * Theme values come from `useResolvedTheme()` rather than the ambient provider,
 * so an overlay opened inside a compare-view poster follows that poster's
 * forced theme.
 */
export const OverlayLayer = ({ children }: { children: ReactNode }) => {
  const { theme, mode, density, reduceMotion } = useResolvedTheme();
  const host = document.getElementById('root') ?? document.body;

  return createPortal(
    <div
      className="ds-root ds-portal-layer"
      data-ds-theme={theme}
      data-ds-mode={mode}
      data-ds-density={density}
      data-ds-reduce-motion={reduceMotion ? 'true' : undefined}
    >
      {children}
    </div>,
    host,
  );
};
